import React, { useEffect, useRef, useState } from 'react';

import { CHART_CONFIG } from 'constants/classTrends';

import {
  calculateChartPoints,
  createPathD,
  formatWeekRange
} from 'utils/teachers/classTrends';

import styles from './styles.module.scss';

const ClassPerformanceTrendsChart = ({
  trends = [],
  activeWeekIndex = 0,
  onSelectWeek = () => {}
}) => {
  const scrollRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(
    CHART_CONFIG.baseSvgWidth || 600
  );

  const { svgHeight, padding, yTicks } = CHART_CONFIG;
  const chartHeight = svgHeight - padding.top - padding.bottom;

  const hasTrends = Array.isArray(trends) && trends.length > 0;

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!hasTrends || !scrollEl) return;

    if (scrollEl.clientWidth > 0) {
      setContainerWidth(scrollEl.clientWidth);
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    });

    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [hasTrends]);

  const {
    points = [],
    svgWidth = containerWidth,
    isScrollable = false
  } = hasTrends
    ? calculateChartPoints({ trends, config: CHART_CONFIG, containerWidth })
    : {};

  const activePoint = points[activeWeekIndex] || points[0];

  const celebratePath = hasTrends
    ? createPathD({ points, key: 'celebrateY' })
    : '';
  const supportPath = hasTrends ? createPathD({ points, key: 'supportY' }) : '';
  const intervenePath = hasTrends
    ? createPathD({ points, key: 'interveneY' })
    : '';

  useEffect(() => {
    if (isScrollable && scrollRef.current && activePoint) {
      const containerW = scrollRef.current.clientWidth;
      const targetScrollLeft = activePoint.x - containerW / 2;

      scrollRef.current.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: 'smooth'
      });
    }
  }, [activePoint, isScrollable, activeWeekIndex]);

  return (
    <div className={styles.chartWrapper}>
      {!hasTrends ? (
        <div className={styles.emptyChartState}>
          <span>No trend data available for this class.</span>
        </div>
      ) : (
        <>
          <div className={styles.yAxisColumn} style={{ height: svgHeight }}>
            {yTicks.map((tick) => {
              const yPos =
                padding.top + chartHeight - (tick / 100) * chartHeight;
              return (
                <span
                  key={`y-label-${tick}`}
                  className={styles.yAxisLabel}
                  style={{ top: `${yPos}px` }}
                >
                  {tick === 0 ? '0' : `${tick}%`}
                </span>
              );
            })}
          </div>

          <div className={styles.chartScrollArea} ref={scrollRef}>
            <svg
              className={styles.svgChart}
              width={isScrollable ? svgWidth : '100%'}
              height={svgHeight}
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            >
              {yTicks.map((tick) => {
                const yPos =
                  padding.top + chartHeight - (tick / 100) * chartHeight;
                return (
                  <line
                    key={`y-grid-${tick}`}
                    x1={padding.left}
                    y1={yPos}
                    x2={svgWidth - padding.right}
                    y2={yPos}
                    className={styles.gridLine}
                  />
                );
              })}

              {points.map((point, idx) => (
                <g key={`x-grid-${idx}`} className={styles.gridGroup}>
                  <line
                    x1={point.x}
                    y1={padding.top}
                    x2={point.x}
                    y2={padding.top + chartHeight}
                    className={styles.verticalGridLine}
                  />
                  <text
                    x={point.x}
                    y={svgHeight - 8}
                    className={`${styles.xAxisLabel} ${
                      idx === activeWeekIndex ? styles.activeXLabel : ''
                    }`}
                    onClick={() =>
                      onSelectWeek({ index: idx, data: point.data })
                    }
                  >
                    {formatWeekRange({
                      weekStart: point.data.week_start,
                      weekEnd: point.data.week_end
                    })}
                  </text>
                </g>
              ))}

              {activePoint && (
                <line
                  x1={activePoint.x}
                  y1={padding.top}
                  x2={activePoint.x}
                  y2={padding.top + chartHeight}
                  className={styles.activeGuideline}
                />
              )}

              {celebratePath && (
                <path d={celebratePath} className={styles.lineCelebrate} />
              )}
              {supportPath && (
                <path d={supportPath} className={styles.lineSupport} />
              )}
              {intervenePath && (
                <path d={intervenePath} className={styles.lineIntervene} />
              )}

              {points.map((point, idx) => {
                const isActive = idx === activeWeekIndex;
                const radius = isActive ? 6 : 4;
                const activeClass = isActive ? styles.activeDot : '';

                return (
                  <g key={`points-${idx}`} className={styles.pointGroup}>
                    <circle
                      cx={point.x}
                      cy={point.celebrateY}
                      r={radius}
                      className={`${styles.dot} ${styles.dotCelebrate} ${activeClass}`}
                      onClick={() =>
                        onSelectWeek({ index: idx, data: point.data })
                      }
                    />
                    <circle
                      cx={point.x}
                      cy={point.supportY}
                      r={radius}
                      className={`${styles.dot} ${styles.dotSupport} ${activeClass}`}
                      onClick={() =>
                        onSelectWeek({ index: idx, data: point.data })
                      }
                    />
                    <circle
                      cx={point.x}
                      cy={point.interveneY}
                      r={radius}
                      className={`${styles.dot} ${styles.dotIntervene} ${activeClass}`}
                      onClick={() =>
                        onSelectWeek({ index: idx, data: point.data })
                      }
                    />
                  </g>
                );
              })}
            </svg>
          </div>
        </>
      )}
    </div>
  );
};

export default ClassPerformanceTrendsChart;
